import React, { useState, useEffect, useCallback, useRef } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import RaffleApi from "../../../services/resources/RaffleApi";
import axios from "axios";
import InfiniteLoadList from "../../common/InfiniteLoadList";
import {
  formatCurrency,
  formatNumber,
  pluralizeText,
} from "../../../lib/utils";
import { useDebounce } from "../../../lib/hooks/useDebounce";
dayjs.extend(relativeTime);

const teamLimit = 9;
const donorLimit = 20;

export default function DonorsList(props) {
  const [donors, setDonors] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("DateDesc");
  const latestRecordRef = useRef();
  const {
    currencyCode,
    donorListDisplay,
    donorsCount,
    fullWidthDisplay,
    newDonorsCount,
    sellerEnrollmentId,
    setDonorsCount,
    setNewDonorsCount,
    team,
    teamURL,
  } = props;

  const recordLimit = team ? teamLimit : donorLimit;

  const sortOptions = [
    { value: "DateDesc", name: "Newest" },
    { value: "DateAsc", name: "Oldest" },
    { value: "AmountDesc", name: "Highest" },
    { value: "AmountAsc", name: "Lowest" },
  ];

  const apiSignal = axios.CancelToken.source();

  const getDonors = useCallback(
    async (page = 1, search = searchText, sort = sortBy) => {
      setLoading(true);
      setPage(page);

      try {
        const response = await RaffleApi.getDonors(
          apiSignal.token,
          sellerEnrollmentId,
          page,
          recordLimit,
          sort,
          search,
          team ? team.id : undefined
        );

        const donorsResults =
          page === 1 ? response.results : [...donors, ...response.results];

        setDonors(donorsResults);
        setNewDonorsCount(0);
        // only set donorsCount if there is no search text, otherwise it's a filtered count and does not reflect the actual donors count
        if (!search) setDonorsCount(response.numberOfRows);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setError(err);
        }
      }
      setLoading(false);
    }, // eslint-disable-next-line
    [apiSignal.token, sellerEnrollmentId, sortBy, searchText, team]
  );

  const debouncedGetDonors = useDebounce(getDonors);
  const onFilterDonors = useCallback(
    async (e) => {
      const { value } = e.target;
      setSearchText(value);
      debouncedGetDonors(1, value);
    },
    [debouncedGetDonors]
  );

  const onSortDonors = useCallback(
    (e) => {
      const { value } = e.target;
      setSortBy(value);
      getDonors(1, undefined, value);
    },
    [getDonors]
  );
  const onRefreshDonors = useCallback(() => {
    getDonors();
  }, [getDonors]);

  useEffect(() => {
    getDonors();
    return () => {
      apiSignal.cancel();
    };
    // eslint-disable-next-line
  }, []);

  return (
    !!donorsCount && (
      <div className={fullWidthDisplay ? "team-donor-list" : "donor-list"}>
        <div className="flex flex-justify-space flex-align-center mb-16 mobile-block">
          <p className="fs-28 fw-600 brand-color-text">
            {team ? "Team Donors " : "Donors "}
            <span className="fs-18 fw-400 accent-text">
              {donorsCount ? `(${formatNumber(donorsCount)})` : ""}
            </span>
          </p>
          <div className="flex flex-align-center">
            {newDonorsCount ? (
              <div className="fs-10 pointer" onClick={onRefreshDonors}>
                {`Load ${newDonorsCount}  ${pluralizeText(
                  "new donor",
                  newDonorsCount
                )}`}
              </div>
            ) : (
              ""
            )}
            {fullWidthDisplay && (
              <div className="donor-list-filters ml-16 mobile-ml-0">
                <div className="search-input">
                  <i className="material-icons accent-text">search</i>
                  <input
                    onChange={onFilterDonors}
                    placeholder="Search donors"
                    type="text"
                    value={searchText}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="donor-list-filters">
          {!fullWidthDisplay && (
            <div className="search-input">
              <i className="material-icons accent-text">search</i>
              <input
                onChange={onFilterDonors}
                placeholder="Search donors"
                type="text"
                value={searchText}
              />
            </div>
          )}
          <div className="select-container">
            <p className="fs-12 accent-text">Sort by</p>
            <select
              className="custom-select"
              onChange={onSortDonors}
              value={sortBy}
            >
              {sortOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <InfiniteLoadList
          error={error}
          getRecords={getDonors}
          onRefreshDonors={onRefreshDonors}
          loading={loading}
          name="Donors"
          noRecordsDisplay={<div className="donor-list-items" />}
          page={page}
          records={donors}
          recordsTotal={donorsCount}
          renderRecords={(records) => (
            <div className="donor-list-items">
              {records.map((d, index) => (
                <div
                  className="donor-list-item"
                  key={index}
                  ref={
                    index === records.length - 1 ? latestRecordRef : undefined
                  } //when loading more, scroll to latest result
                >
                  <div className="fw-600 mb-8 flex flex-justify-space">
                    <p>
                      {d.isPrivate
                        ? "Anonymous"
                        : `${d.donorFirstName} ${d.donorLastName}`}
                    </p>
                    <p>
                      {donorListDisplay === "Tickets"
                        ? `${d.quantity} ${pluralizeText("Ticket", d.quantity)}`
                        : formatCurrency(d.amount, currencyCode)}
                    </p>
                  </div>
                  {!team && d.teamName && (
                    <p className="fs-12 mt-8">
                      with{" "}
                      <a
                        href={teamURL(d.teamName)}
                        className="pointer underline-text"
                      >
                        {d.teamName}
                      </a>
                    </p>
                  )}
                  {d.tribute && (
                    <p className="fs-12 accent-text italic-text mt-8">
                      {" "}
                      {d.tribute}
                    </p>
                  )}
                  <p className="fs-12 accent-text mt-16">
                    {dayjs(new Date(d.createdOn)).fromNow()}
                  </p>
                </div>
              ))}
              {loading && <p className="text-center mt-16">Loading...</p>}
            </div>
          )}
          showLoader={false}
          showLoadMore={!searchText}
          manualLoad
          latestRecordRef={latestRecordRef}
        />
      </div>
    )
  );
}
